/**
 * API Routes Constants
 * Local copies of the groups below, except IMPORT_ROUTES, which is re-exported from
 * @bat/shared: its build, prerender and tests (#3882) show no ESM/CJS problem, and the
 * copy had drifted. The local groups should match @bat/shared/routes/
 */

// Config Routes
export const CONFIG_ROUTES = {
  BASE: '/api/config',
  CONSTANTS: '/api/config/constants',
  BUSINESS_CONSTANTS: '/api/config/constants/business',
  UI_CONSTANTS: '/api/config/constants/ui',
  VALIDATION_CONSTANTS: '/api/config/constants/validation',
  HEALTH: '/api/config/health',
  SUPPORTED_ASSETS: '/api/config/supported-assets',
  SUPPORTED_EXCHANGES: '/api/config/supported-exchanges',
  VALIDATION_RULES: '/api/config/validation-rules',
  ASSET_CATEGORIES: '/api/config/asset-categories',
  // Catalog endpoints — dev-curated static lists, served via API so the
  // frontend never hardcodes the same data (PR M-C).
  PROFILE_OPTIONS: '/api/config/profile-options',
  INTEGRATION_SOURCES: '/api/config/integration-sources',
} as const

// I18n Routes
export const I18N_ROUTES = {
  BASE: '/api/i18n',
  MANIFEST: (locale: string) => `/api/i18n/manifest/${locale}`,
  BUNDLE: (locale: string, audience: string, hash: string) =>
    `/api/i18n/bundle/${locale}/${audience}/${hash}`,
  INFO: '/api/i18n/info',
  HEALTH: '/api/i18n/health',
} as const

// Balance Routes
export const BALANCE_ROUTES = {
  BASE: '/api/balances',
  LIST: '/api/balances',
  SUMMARY: '/api/balances/summary',
  EXPORT: '/api/balances/export',
  VALIDATE: '/api/balances/validate',
  RATES: '/api/balances/rates',
  RATES_BY_CURRENCY: (currency: string) => `/api/balances/rates/${currency}`,
  SUPPORTED_CURRENCIES: '/api/balances/supported-currencies',
  METADATA: '/api/balances/metadata',
  // El parámetro es el SLUG del exchange ('binance', 'hyperliquid'), que es lo
  // que estas llamadas mandaban ya (`ExchangeBalance.exchangeId`). El servidor
  // lo llamaba `:platformId` y lo traducía a una fila de `bat.platforms` que no
  // existía nunca (#2179); ahora el slug es la identidad de punta a punta.
  VERIFY: (exchangeSlug: string) => `/api/balances/verify/${exchangeSlug}`,
  VERIFICATION_LATEST: (exchangeSlug: string) =>
    `/api/balances/verification/${exchangeSlug}/latest`,
} as const

// Tax Report Routes
export const TAX_REPORT_ROUTES = {
  BASE: '/api/tax-reports',
  LIST: '/api/tax-reports',
  CREATE: '/api/tax-reports',
  GENERATE: '/api/tax-reports/generate',
  GET_BY_ID: (id: string | number) => `/api/tax-reports/${id}`,
  UPDATE: (id: string | number) => `/api/tax-reports/${id}`,
  DELETE: (id: string | number) => `/api/tax-reports/${id}`,
  STATUS: (id: string | number) => `/api/tax-reports/${id}/status`,
  CANCEL: (id: string | number) => `/api/tax-reports/${id}/cancel`,
  MARK_SUBMITTED: (id: string | number) => `/api/tax-reports/${id}/mark-submitted`,
  DOWNLOAD: (id: string | number) => `/api/tax-reports/${id}/download`,
  DOWNLOAD_PDF: (id: string | number) => `/api/tax-reports/${id}/download-pdf`,
  DOWNLOAD_XLSX: (id: string | number) => `/api/tax-reports/${id}/download-xlsx`,
  DOWNLOAD_CSV: (id: string | number) => `/api/tax-reports/${id}/csv`,
  // Official government tax-authority export (AEAT Modelo 100, IRS Form 8949,
  // HMRC …). `format` is a wire slug: `8949 | modelo100 | hmrc | generic`.
  EXPORT_OFFICIAL: (id: string | number, country: string, format: string) =>
    `/api/tax-reports/${id}/export/${country}/${format}`,
  // Pre-generation cost-basis consistency check (CostBasisConsistencyValidator).
  // Read-only when called with `saveIssues=false`. NOTE: there is no
  // `POST /api/tax-reports/validate` on the backend — the old VALIDATE constant
  // pointed at a route that has never existed.
  VALIDATION_BEFORE_GENERATE: '/api/tax-reports/validation/before-generate',
  AVAILABLE_YEARS: '/api/tax-reports/available-years',
  STATS: '/api/tax-reports/stats',
  CONFIG: '/api/tax-config',
  CONFIG_COUNTRIES: '/api/tax-config/countries',
  CONFIG_BY_COUNTRY: (country: string) => `/api/tax-config/${country}`,
  // Phase 4b of Issue #358 — tenant-facing effective tax-config (per-country
  // limits resolved from bat.tax_configurations; drives UI input clamps).
  EFFECTIVE_CONFIG: '/api/tax-reports/effective-config',
  // Detail endpoints for modal tabs
  DETAILS: (id: string | number) => `/api/tax-reports/${id}/details`,
  CALCULATIONS: (id: string | number) => `/api/tax-reports/${id}/calculations`,
  ATTRIBUTIONS: (id: string | number, calculationId: string) =>
    `/api/tax-reports/${id}/attributions/${calculationId}`,
  BY_ASSET: (id: string | number) => `/api/tax-reports/${id}/by-asset`,
} as const

// Import Routes
// Not a local copy: `@bat/shared` is the single source (the copy had drifted: 16 keys
// only here, 7 only there). Vite bundles it like every other `@bat/shared` import.
export { IMPORT_ROUTES } from '@bat/shared'

// Asset Routes
// Served by `modules/accounting/core/assets/api/routes.ts` on the backend.
// `ALIASES` / `DELETE_ALIAS` reach `bat.tenant_asset_aliases` — the caller's own
// ticker→asset rules written by the import "Needs attention" resolver.
export const ASSET_ROUTES = {
  BASE: '/api/assets',
  SEARCH: '/api/assets/search',
  TENANT: '/api/assets/tenant',
  ALIASES: '/api/assets/aliases',
  DELETE_ALIAS: (aliasId: string) => `/api/assets/aliases/${aliasId}`,
} as const
